/**
 * One <video> per source, its files appended into one SourceBuffer at their
 * offsets (design §7.2). Loading starts at the file under the playhead and
 * keeps a window around it, so a day of recordings fits MSE quotas
 * (Chromium allows about 150 MB of video).
 *
 * @module
 */
import type { ProxyTiming } from "../api/client";
import type { Track } from "./controller";
import { fileAt, type PlanFile } from "./plan";
import { fragmentAt, mfraSize, parseMfra, timescales, type FragmentIndex } from "./fragments";

const CHUNK_BYTES = 2 * 1024 * 1024;
/** Stop fetching this far ahead of the playhead... */
const AHEAD_S = 90;
/** ...and drop what is further behind than this. */
const BEHIND_S = 30;
const WAIT_MS = 500;
/** Enough for a proxy's ftyp and moov. */
const HEAD_BYTES = 64 * 1024;
/** A seek this far past what the current load has reached starts a new one there. */
const RESTART_AHEAD_S = 10;

export interface ProxySource {
  url(fileId: string): string;
  audioUrl(fileId: string): string;
  timing(fileId: string): Promise<ProxyTiming>;
}

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

export class VideoChain {
  readonly video = document.createElement("video");
  readonly track: Track;
  /** The last loading failure, for display. */
  error: string | null = null;
  private readonly origin: number;
  private readonly source = new MediaSource();
  private readonly objectUrl = URL.createObjectURL(this.source);
  private buffer: SourceBuffer | null = null;
  private generation = 0;
  private pumping = -1;
  /** Element time the current load started from, and has appended up to. */
  private pumpFrom = 0;
  private pumpAt = 0;
  private readonly indexes = new Map<string, Promise<FragmentIndex | null>>();
  private running: Promise<void> = Promise.resolve();
  private fetches = new AbortController();
  private closed = false;
  private readonly coverage: { fromUtcMs: number; toUtcMs: number }[];
  private readonly files: PlanFile[];
  /** Element time where each fully appended file's media ends. */
  private readonly loadedEnd = new Map<number, number>();

  constructor(
    files: readonly PlanFile[],
    private readonly proxies: ProxySource,
    private readonly mime: string,
  ) {
    this.files = [...files];
    this.origin = files[0]?.fromUtcMs ?? 0;
    this.video.style.width = "100%";
    this.video.style.height = "100%";
    this.video.style.objectFit = "contain";
    this.video.playsInline = true;
    this.coverage = files.map((f) => ({
      fromUtcMs: f.fromUtcMs,
      toUtcMs: f.toUtcMs,
    }));
    this.track = {
      media: this.video,
      startUtcMs: this.origin,
      coverage: this.coverage,
    };
  }

  /** Makes sure the file at (or next after) `utcMs` is loading or loaded. */
  want(utcMs: number): void {
    if (this.closed) return;
    let i = fileAt(this.files, utcMs);
    const file = this.files[i];
    if (!file) return;
    let target = (Math.max(utcMs, file.fromUtcMs) - this.origin) / 1000;
    // A file buffered to its end hands over to the next, whose start may be
    // missing: seeking ahead and back leaves the next file loaded only from
    // the later point, and the clock would hold at the gap for ever.
    for (;;) {
      const end = this.loadedEnd.get(i);
      const r = this.rangeAt(target);
      const next = this.files[i + 1];
      if (!next || end === undefined || !r || r[1] < end - 0.05) break;
      const nextFrom = (next.fromUtcMs - this.origin) / 1000;
      if (nextFrom - target > AHEAD_S || this.bufferedWithin(nextFrom, nextFrom + 2)) return;
      i += 1;
      target = nextFrom;
    }
    if (i === this.pumping && target >= this.pumpFrom - 0.5 && target <= this.pumpAt + RESTART_AHEAD_S) return;
    if (this.isBuffered(i, target)) return;
    const gen = ++this.generation;
    this.pumping = i;
    this.pumpFrom = this.pumpAt = target;
    this.fetches.abort();
    this.fetches = new AbortController();
    const signal = this.fetches.signal;
    this.running = this.running
      .then(() => this.pump(i, target, gen, signal))
      .catch((e: unknown) => {
        if (gen === this.generation && !signal.aborted) this.error = String(e);
      });
  }

  /** Appends files after the chain's last, e.g. from the next day. */
  addFiles(files: readonly PlanFile[]): void {
    const last = this.files[this.files.length - 1];
    for (const f of files) {
      if (last && f.fromUtcMs < last.toUtcMs) continue;
      this.files.push(f);
      this.coverage.push({ fromUtcMs: f.fromUtcMs, toUtcMs: f.toUtcMs });
    }
    this.extendDuration();
  }

  /** WebKit clamps seeks to the duration, so it must cover every file. */
  private extendDuration(): void {
    const last = this.files[this.files.length - 1];
    const want = last ? (last.toUtcMs - this.origin) / 1000 + 60 : 0;
    const d = this.source.duration;
    if (this.source.readyState !== "open" || this.buffer?.updating || d >= want) return;
    this.source.duration = want;
  }

  /** Puts the element in `host`, attaching the MediaSource the first time. */
  attach(host: HTMLElement): void {
    host.append(this.video);
    if (!this.video.src) this.video.src = this.objectUrl;
  }

  close(): void {
    this.closed = true;
    this.generation++;
    this.fetches.abort();
    this.video.removeAttribute("src");
    this.video.load();
    URL.revokeObjectURL(this.objectUrl);
  }

  private async sourceBuffer(): Promise<SourceBuffer> {
    if (this.buffer) return this.buffer;
    if (this.source.readyState !== "open") {
      await new Promise((r) => this.source.addEventListener("sourceopen", r, { once: true }));
    }
    // Muted only now: WebKit fails every append ("Failed to push buffer")
    // for an element muted before its MediaSource opened.
    this.video.muted = true;
    this.buffer ??= this.source.addSourceBuffer(this.mime);
    // Finite: WebKit treats an infinite duration as live.
    this.extendDuration();
    return this.buffer;
  }

  /** Loads files from `from`, the first starting at the fragment holding element time `target`. */
  private async pump(from: number, target: number, gen: number, signal: AbortSignal): Promise<void> {
    const buffer = await this.sourceBuffer();
    for (let i = from; i < this.files.length && gen === this.generation; i++) {
      const file = this.files[i];
      if (!file) break;
      this.pumping = i;
      this.extendDuration();
      const timing = await this.proxies.timing(file.id);
      const url = this.proxies.url(file.id);
      const index = await this.index(file.id, signal);
      if (gen !== this.generation) return;
      const offsetS = (file.fromUtcMs - this.origin) / 1000 + (timing.video_start_s ?? 0);
      // A file left half-appended leaves the parser mid-box.
      if (this.source.readyState === "open") buffer.abort();
      buffer.timestampOffset = offsetS;
      let start = 0;
      let end = Infinity;
      if (index) {
        end = index.endBytes;
        start = i === from ? fragmentAt(index, target - offsetS) : 0;
        if (start > 0) {
          const init = await range(url, 0, index.initBytes, signal);
          if (gen !== this.generation) return;
          await this.append(buffer, init.data, gen);
        }
      }
      for (let offset = start; offset < end;) {
        while (this.aheadS() > AHEAD_S) {
          await sleep(WAIT_MS);
          if (gen !== this.generation) return;
        }
        const res = await range(url, offset, Math.min(offset + CHUNK_BYTES, end), signal);
        if (gen !== this.generation) return;
        end = Math.min(end, res.total);
        await this.append(buffer, res.data, gen);
        offset += res.data.byteLength;
        this.pumpAt = Math.max(this.pumpAt, this.bufferedEnd());
      }
      this.fileLoaded(i, buffer);
    }
    if (gen === this.generation) this.pumping = -1;
  }

  /** The proxy's fragment index, or null if it has none (it is then read from the start). */
  private index(fileId: string, signal: AbortSignal): Promise<FragmentIndex | null> {
    let p = this.indexes.get(fileId);
    if (!p) {
      p = readIndex(this.proxies.url(fileId), signal).catch(() => null);
      this.indexes.set(fileId, p);
      void p.then((x) => x ?? this.indexes.delete(fileId));
    }
    return p;
  }

  private bufferedEnd(): number {
    const b = this.buffer?.buffered;
    return b && b.length > 0 ? b.end(b.length - 1) : 0;
  }

  /** Appends, first dropping data behind the playhead, and again on QuotaExceededError until there is room. */
  private async append(buffer: SourceBuffer, data: ArrayBuffer, gen: number): Promise<void> {
    for (;;) {
      await this.evictBehind(buffer);
      try {
        await whenUpdated(buffer, () => buffer.appendBuffer(data));
        return;
      } catch (e) {
        if (!(e instanceof DOMException && e.name === "QuotaExceededError")) throw e;
        await sleep(WAIT_MS);
        if (gen !== this.generation) return;
      }
    }
  }

  private async evictBehind(buffer: SourceBuffer): Promise<void> {
    const keepFrom = this.video.currentTime - BEHIND_S;
    const ranges = buffer.buffered;
    if (ranges.length > 0 && ranges.start(0) < keepFrom) {
      await whenUpdated(buffer, () => buffer.remove(0, keepFrom));
    }
    const playing = this.rangeAt(this.video.currentTime);
    const after = playing ? playing[1] : this.video.currentTime;
    const last = buffer.buffered.length > 0 ? buffer.buffered.end(buffer.buffered.length - 1) : 0;
    // Data appended for an earlier seek target far ahead.
    if (last > after + AHEAD_S * 2) {
      await whenUpdated(buffer, () => buffer.remove(after + AHEAD_S, Infinity));
    }
  }

  private aheadS(): number {
    const t = this.video.currentTime;
    const r = this.rangeAt(t);
    return r ? r[1] - t : 0;
  }

  /**
   * Records where file `i` really ends, and shortens its coverage to match:
   * a file whose end was unknown was assumed to run on (plan.ts).
   */
  private fileLoaded(i: number, buffer: SourceBuffer): void {
    const file = this.files[i];
    const cov = this.coverage[i];
    if (!file || !cov) return;
    const from = (file.fromUtcMs - this.origin) / 1000;
    const next = this.files[i + 1];
    const nextFrom = next ? (next.fromUtcMs - this.origin) / 1000 : Infinity;
    let end = from;
    for (let k = 0; k < buffer.buffered.length; k++) {
      const [s, e] = [buffer.buffered.start(k), buffer.buffered.end(k)];
      if (s < nextFrom && e > end) end = Math.min(e, nextFrom);
    }
    this.loadedEnd.set(i, end);
    cov.toUtcMs = Math.min(file.toUtcMs, this.origin + end * 1000);
  }

  private isBuffered(i: number, t: number): boolean {
    const r = this.rangeAt(t);
    if (!r) return false;
    const end = this.loadedEnd.get(i);
    return r[1] - t > 1 || (end !== undefined && r[1] >= end - 0.05);
  }

  private bufferedWithin(from: number, to: number): boolean {
    const b = this.buffer?.buffered;
    if (!b) return false;
    for (let k = 0; k < b.length; k++) if (b.start(k) < to && b.end(k) > from) return true;
    return false;
  }

  private rangeAt(t: number): [number, number] | null {
    const b = this.buffer?.buffered;
    if (!b) return null;
    for (let i = 0; i < b.length; i++) {
      if (b.start(i) <= t + 0.1 && t < b.end(i)) return [b.start(i), b.end(i)];
    }
    return null;
  }
}

/** Bytes [from, to) of `url`, and the file's total size. */
export async function range(
  url: string,
  from: number,
  to: number,
  signal: AbortSignal,
): Promise<{ data: ArrayBuffer; total: number }> {
  const res = await fetch(url, {
    headers: { Range: `bytes=${from}-${to - 1}` },
    signal,
  });
  if (res.status !== 206) throw new Error(`${url}: expected 206, got ${res.status}`);
  const total = Number(res.headers.get("content-range")?.split("/")[1] ?? NaN);
  const data = await res.arrayBuffer();
  if (data.byteLength === 0 || !Number.isFinite(total)) throw new Error(`${url}: bad range response`);
  return { data, total };
}

export async function readIndex(url: string, signal: AbortSignal): Promise<FragmentIndex | null> {
  const head = await range(url, 0, HEAD_BYTES, signal);
  const tail = await range(url, head.total - 16, head.total, signal);
  const size = mfraSize(tail.data);
  if (!size || size > head.total) return null;
  const mfra = await range(url, head.total - size, head.total, signal);
  const parsed = parseMfra(mfra.data);
  const scale = parsed && timescales(head.data).get(parsed.trackId);
  const first = parsed?.entries[0];
  if (!parsed || !scale || !first || first.offset > head.data.byteLength) return null;
  return {
    initBytes: first.offset,
    endBytes: head.total - size,
    fragments: parsed.entries.map((e) => ({
      t: e.ticks / scale,
      offset: e.offset,
    })),
  };
}

function whenUpdated(buffer: SourceBuffer, start: () => void): Promise<void> {
  return new Promise((resolve, reject) => {
    const done = () => {
      buffer.removeEventListener("updateend", done);
      buffer.removeEventListener("error", failed);
      resolve();
    };
    const failed = () => {
      buffer.removeEventListener("updateend", done);
      buffer.removeEventListener("error", failed);
      reject(new Error("SourceBuffer update failed"));
    };
    buffer.addEventListener("updateend", done);
    buffer.addEventListener("error", failed);
    try {
      start();
    } catch (e) {
      buffer.removeEventListener("updateend", done);
      buffer.removeEventListener("error", failed);
      reject(e instanceof Error ? e : new Error(String(e)));
    }
  });
}
